import { useEffect, useMemo, useRef, useState } from 'react';
import { useAnimationFrame } from '../useAnimationFrame';
import {
  cueHighlights,
  cueIsActive,
  NowYouTry,
  usePrefersReducedMotion,
  useMountSequence,
  VizCard,
  type VizCue,
} from '../vizShared';

// SolubilityRules — pick a cation + an anion and predict whether the combination
// is soluble (stays dissolved as free ions) or forms a precipitate. When it
// precipitates, ions migrate together into a settling solid (animated); when
// soluble, ions keep drifting freely. A rules reference panel + a net-ionic hint
// reinforce the reasoning.
//
// config: { cation?, anion?, title? } — both optional; defaults to a pair that
// precipitates (Ag⁺ + Cl⁻ → AgCl). Never crashes on unknown ions.

export interface SolubilityConfig {
  title?: string;
  cation?: string;
  anion?: string;
}

interface Props {
  config?: SolubilityConfig;
  onComplete?: (result?: unknown) => void;
  cue?: VizCue;
}

interface Ion {
  id: string;
  label: string; // display with charge
  charge: number;
}

const CATIONS: Ion[] = [
  { id: 'Na', label: 'Na⁺', charge: 1 },
  { id: 'K', label: 'K⁺', charge: 1 },
  { id: 'Li', label: 'Li⁺', charge: 1 },
  { id: 'NH4', label: 'NH₄⁺', charge: 1 },
  { id: 'Ag', label: 'Ag⁺', charge: 1 },
  { id: 'Ca', label: 'Ca²⁺', charge: 2 },
  { id: 'Sr', label: 'Sr²⁺', charge: 2 },
  { id: 'Ba', label: 'Ba²⁺', charge: 2 },
  { id: 'Mg', label: 'Mg²⁺', charge: 2 },
  { id: 'Pb', label: 'Pb²⁺', charge: 2 },
  { id: 'Cu', label: 'Cu²⁺', charge: 2 },
  { id: 'Fe2', label: 'Fe²⁺', charge: 2 },
  { id: 'Fe', label: 'Fe³⁺', charge: 3 },
  { id: 'Al', label: 'Al³⁺', charge: 3 },
];

const ANIONS: Ion[] = [
  { id: 'NO3', label: 'NO₃⁻', charge: -1 },
  { id: 'Cl', label: 'Cl⁻', charge: -1 },
  { id: 'Br', label: 'Br⁻', charge: -1 },
  { id: 'I', label: 'I⁻', charge: -1 },
  { id: 'OH', label: 'OH⁻', charge: -1 },
  { id: 'C2H3O2', label: 'CH₃COO⁻', charge: -1 },
  { id: 'SO4', label: 'SO₄²⁻', charge: -2 },
  { id: 'SO3', label: 'SO₃²⁻', charge: -2 },
  { id: 'CrO4', label: 'CrO₄²⁻', charge: -2 },
  { id: 'CO3', label: 'CO₃²⁻', charge: -2 },
  { id: 'PO4', label: 'PO₄³⁻', charge: -3 },
  { id: 'S', label: 'S²⁻', charge: -2 },
];

const ALWAYS_SOLUBLE_CATIONS = new Set(['Na', 'K', 'Li', 'NH4']);

// Standard general solubility rules → returns soluble + the rule cited.
function predict(cat: string, an: string): { soluble: boolean; rule: string } {
  // Group 1 + ammonium salts: always soluble (overrides exceptions).
  if (ALWAYS_SOLUBLE_CATIONS.has(cat)) {
    return { soluble: true, rule: 'Group 1 (Na⁺, K⁺) and NH₄⁺ salts are always soluble.' };
  }
  switch (an) {
    case 'NO3':
      return { soluble: true, rule: 'All nitrates (NO₃⁻) are soluble.' };
    case 'C2H3O2':
      return { soluble: true, rule: 'Acetates (CH₃COO⁻) are soluble.' };
    case 'Cl':
    case 'Br':
    case 'I':
      if (cat === 'Ag' || cat === 'Pb')
        return { soluble: false, rule: 'Halides are soluble EXCEPT with Ag⁺, Pb²⁺ (and Hg₂²⁺).' };
      return { soluble: true, rule: 'Chlorides, bromides, iodides are soluble (except Ag⁺, Pb²⁺, Hg₂²⁺).' };
    case 'SO4':
      if (['Ba', 'Pb', 'Ca', 'Sr'].includes(cat))
        return { soluble: false, rule: 'Sulfates are soluble EXCEPT with Ba²⁺, Pb²⁺, Ca²⁺, Sr²⁺.' };
      return { soluble: true, rule: 'Most sulfates are soluble (except Ba²⁺, Pb²⁺, Ca²⁺, Sr²⁺).' };
    case 'OH':
      if (['Ca', 'Ba', 'Sr'].includes(cat))
        return { soluble: true, rule: 'Hydroxides are insoluble except Group 1, NH₄⁺, and slightly Ca²⁺/Ba²⁺/Sr²⁺.' };
      return { soluble: false, rule: 'Hydroxides (OH⁻) are insoluble except Group 1, NH₄⁺, Ba²⁺.' };
    case 'CO3':
    case 'PO4':
    case 'S':
      return { soluble: false, rule: 'Carbonates, phosphates, and sulfides are insoluble except with Group 1 / NH₄⁺.' };
    case 'SO3':
      return { soluble: false, rule: 'Sulfites (SO₃²⁻) are insoluble except with Group 1 / NH₄⁺.' };
    case 'CrO4':
      if (cat === 'Mg')
        return { soluble: true, rule: 'Most chromates are insoluble; MgCrO₄ is soluble.' };
      return { soluble: false, rule: 'Chromates (CrO₄²⁻) are insoluble except Group 1 / NH₄⁺ (and Mg²⁺).' };
    default:
      return { soluble: true, rule: 'No exception applies — treated as soluble.' };
  }
}

// Build the empirical formula label using charge-balancing subscripts.
function formula(cat: Ion, an: Ion): string {
  const c = Math.abs(cat.charge);
  const a = Math.abs(an.charge);
  const g = gcd(c, a);
  const nc = a / g;
  const na = c / g;
  const catCore = cat.label.replace(/[⁺²³⁴]/g, '');
  const anCore = an.label.replace(/[⁻²³⁴]/g, '');
  const sub = (core: string, n: number) => {
    if (n === 1) return core;
    // wrap polyatomic (contains lowercase or >1 uppercase) in parentheses
    const poly = /[a-z]/.test(core) || (core.match(/[A-Z]/g)?.length ?? 0) > 1;
    return poly ? `(${core})${toSub(n)}` : `${core}${toSub(n)}`;
  };
  return `${sub(catCore, nc)}${sub(anCore, na)}`;
}
function gcd(a: number, b: number): number { return b === 0 ? a : gcd(b, a % b); }
const SUBS: Record<string, string> = { '0': '₀', '1': '₁', '2': '₂', '3': '₃', '4': '₄', '5': '₅', '6': '₆', '7': '₇', '8': '₈', '9': '₉' };
function toSub(n: number) { return String(n).split('').map((d) => SUBS[d] ?? d).join(''); }

const W = 280;
const H = 170;

interface P { x: number; y: number; vx: number; vy: number; tx: number; ty: number; cat: boolean; }

// Curated example pairs for the self-demo + cue.step (one precipitate, one soluble…).
const EXAMPLES: Array<{ cat: string; an: string }> = [
  { cat: 'Ag', an: 'Cl' }, // AgCl — precipitate
  { cat: 'Na', an: 'Cl' }, // NaCl — soluble
  { cat: 'Ba', an: 'SO4' }, // BaSO4 — precipitate
  { cat: 'K', an: 'NO3' }, // KNO3 — soluble
];

export default function SolubilityRules({ config = {}, onComplete, cue }: Props) {
  const reduced = usePrefersReducedMotion();
  const hasCue = cueIsActive(cue);

  const defaultCat = config.cation && CATIONS.some((c) => c.id === config.cation) ? config.cation : 'Ag';
  const defaultAn = config.anion && ANIONS.some((a) => a.id === config.anion) ? config.anion : 'Cl';
  const [catId, setCatId] = useState(defaultCat);
  const [anId, setAnId] = useState(defaultAn);
  const interactedRef = useRef(false);

  const cat = CATIONS.find((c) => c.id === catId) ?? CATIONS[0];
  const an = ANIONS.find((a) => a.id === anId) ?? ANIONS[0];
  const result = useMemo(() => predict(catId, anId), [catId, anId]);
  const f = formula(cat, an);

  const mark = () => {
    if (!interactedRef.current) {
      interactedRef.current = true;
      onComplete?.({ type: 'solubility' });
    }
  };

  // ---- Self-demo: show a precipitate forming, then a soluble pair, then settle.
  const demo = useMountSequence(
    [
      { at: 250, run: () => { setCatId('Ag'); setAnId('Cl'); } }, // precipitate forms
      { at: 1500, run: () => { setCatId('Na'); setAnId('Cl'); } }, // dissolves
      { at: 2700, run: () => { setCatId(defaultCat); setAnId(defaultAn); } },
    ],
    { skip: reduced || hasCue, onSettle: () => { setCatId(defaultCat); setAnId(defaultAn); } },
  );
  const cancelDemo = () => { if (demo.running) demo.cancel(); };

  // ---- Cue: highlight → spotlight an ion pair; step → step through EXAMPLES.
  useEffect(() => {
    const hi = cueHighlights(cue);
    if (hi.length === 0) return;
    const c = CATIONS.find((x) => hi.some((h) => x.id.toLowerCase() === h || x.label.toLowerCase().includes(h)));
    const a = ANIONS.find((x) => hi.some((h) => x.id.toLowerCase() === h || x.label.toLowerCase().includes(h)));
    if (c) setCatId(c.id);
    if (a) setAnId(a.id);
  }, [cue]);

  useEffect(() => {
    if (cue?.step == null) return;
    const ex = EXAMPLES[Math.max(0, Math.min(EXAMPLES.length - 1, Math.round(cue.step)))];
    setCatId(ex.cat);
    setAnId(ex.an);
  }, [cue?.step]);

  // Particle pool — rebuilt whenever the pair (and thus soluble/insoluble) changes.
  const particles = useMemo<P[]>(() => {
    const arr: P[] = [];
    const n = 7;
    const settleY = H - 22; // bottom band for precipitate
    for (let i = 0; i < n; i++) {
      // precipitate targets line up at the bottom; soluble targets are random
      const tx = result.soluble ? rand(16, W - 16) : 30 + (i / (n - 1)) * (W - 60) + 6;
      const ty = result.soluble ? rand(16, H - 16) : settleY;
      arr.push({ x: rand(16, W - 16), y: rand(16, H - 40), vx: rand(-1, 1), vy: rand(-1, 1), tx, ty, cat: true });
      const tx2 = result.soluble ? rand(16, W - 16) : 30 + (i / (n - 1)) * (W - 60) - 6;
      arr.push({ x: rand(16, W - 16), y: rand(16, H - 40), vx: rand(-1, 1), vy: rand(-1, 1), tx: tx2, ty: result.soluble ? rand(16, H - 16) : settleY + 6, cat: false });
    }
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catId, anId]);

  const partsRef = useRef<P[]>(particles);
  partsRef.current = particles;
  const [, force] = useState(0);

  // Under reduced motion render the resting state immediately (precipitate
  // already settled / ions placed) and skip the animation loop.
  useEffect(() => {
    if (!reduced) return;
    for (const p of partsRef.current) { p.x = p.tx; p.y = p.ty; }
    force((n) => (n + 1) % 1000);
  }, [reduced, catId, anId]);

  useAnimationFrame((dt) => {
    for (const p of partsRef.current) {
      if (result.soluble) {
        // free drift + wall bounce
        p.x += p.vx * 30 * dt;
        p.y += p.vy * 30 * dt;
        if (p.x < 10) { p.x = 10; p.vx = Math.abs(p.vx); }
        if (p.x > W - 10) { p.x = W - 10; p.vx = -Math.abs(p.vx); }
        if (p.y < 10) { p.y = 10; p.vy = Math.abs(p.vy); }
        if (p.y > H - 10) { p.y = H - 10; p.vy = -Math.abs(p.vy); }
      } else {
        // ease toward the precipitate target (ions clump + settle)
        p.x += (p.tx - p.x) * Math.min(1, 2.2 * dt);
        p.y += (p.ty - p.y) * Math.min(1, 2.2 * dt);
      }
    }
    force((n) => (n + 1) % 1000);
  }, !reduced);

  const selBase = 'rounded-sm border px-1.5 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ws-accent focus-visible:ring-offset-1';

  return (
    <VizCard
      reducedMotion={reduced}
      eyebrow="Precipitation"
      title={config.title ?? 'Solubility rules — will it precipitate?'}
      subtitle="Mix a cation with an anion and watch the ions disperse or clump into a solid"
      hint="Pick a cation + anion · auto-demonstrates a precipitate vs a soluble salt on load"
    >
      <div className="w-full select-none">
        {/* Selectors */}
        <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
          <span className="text-ws-ink-3">Cation</span>
          <select value={catId} onChange={(e) => { cancelDemo(); setCatId(e.target.value); mark(); }} className={`${selBase} border-ws-border bg-ws-tile text-ws-ink`} aria-label="Cation">
            {CATIONS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
          <span className="text-ws-ink-3">+</span>
          <span className="text-ws-ink-3">Anion</span>
          <select value={anId} onChange={(e) => { cancelDemo(); setAnId(e.target.value); mark(); }} className={`${selBase} border-ws-border bg-ws-tile text-ws-ink`} aria-label="Anion">
            {ANIONS.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
          </select>
          <span className="ml-1 font-mono text-sm font-semibold text-ws-ink">→ {f}</span>
        </div>

        <div className="relative flex flex-col gap-3 sm:flex-row sm:items-start">
          <NowYouTry show={demo.done && !reduced && !hasCue} />
          {/* Beaker animation */}
          <div className="min-w-0 flex-1">
            <svg viewBox={`0 0 ${W} ${H}`} className="w-full rounded-md border border-ws-border bg-ws-surface-2" role="img" aria-label="Ion behaviour in solution">
              {/* solution fill */}
              <rect x="2" y="2" width={W - 4} height={H - 4} rx="6" fill="#DCE9F2" opacity="0.35" />
              {!result.soluble && (
                <rect x="6" y={H - 26} width={W - 12} height="20" rx="3" fill="var(--ws-ink-4)" opacity="0.15" />
              )}
              {partsRef.current.map((p, i) => (
                <g key={i}>
                  <circle cx={p.x} cy={p.y} r="8" fill={p.cat ? '#5B8FE0' : '#E0864B'} stroke="rgba(0,0,0,0.2)" strokeWidth="1" />
                  <text x={p.x} y={p.y + 2.5} textAnchor="middle" fontSize="6.5" fontWeight="700" fill="#15203A">
                    {p.cat ? cat.label : an.label}
                  </text>
                </g>
              ))}
              <text x={W / 2} y={14} textAnchor="middle" fontSize="9" fontWeight="600" fill="var(--ws-ink-3)">
                {result.soluble ? 'Ions stay dissolved & dispersed' : 'Ions clump → precipitate settles'}
              </text>
            </svg>
          </div>

          {/* Verdict + net ionic */}
          <div className="flex w-full flex-col gap-2 sm:w-52">
            <div
              className={`rounded-md border px-3 py-2 transition-colors ${
                result.soluble ? 'border-ws-success bg-ws-success/10' : 'border-ws-warning bg-ws-warning/10'
              }`}
            >
              <div className="text-xs uppercase tracking-wide text-ws-ink-3">Prediction</div>
              <div className={`text-base font-bold ${result.soluble ? 'text-ws-success' : 'text-ws-warning'}`}>
                {result.soluble ? 'Soluble (aq)' : 'Insoluble — precipitate'}
              </div>
              <div className="mt-1 text-[11px] leading-snug text-ws-ink-2">{result.rule}</div>
            </div>
            <div className="rounded-md border border-ws-border bg-ws-tile px-3 py-2">
              <div className="text-xs uppercase tracking-wide text-ws-ink-3">Net ionic hint</div>
              <div className="mt-0.5 font-mono text-[11px] leading-snug text-ws-ink">
                {result.soluble
                  ? `${cat.label}(aq) + ${an.label}(aq) → no reaction (spectators)`
                  : `${cat.label}(aq) + ${an.label}(aq) → ${f}(s)`}
              </div>
            </div>
          </div>
        </div>

        {/* Rules reference */}
        <details className="mt-3 rounded-md border border-ws-border bg-ws-surface px-3 py-2 text-xs">
          <summary className="cursor-pointer font-semibold text-ws-ink-2">Solubility rules reference</summary>
          <ul className="mt-2 list-disc space-y-1 pl-4 text-ws-ink-2">
            <li>Group 1 (Na⁺, K⁺) and NH₄⁺ salts: <b>soluble</b>.</li>
            <li>Nitrates (NO₃⁻) and acetates (CH₃COO⁻): <b>soluble</b>.</li>
            <li>Cl⁻, Br⁻, I⁻: soluble <b>except</b> Ag⁺, Pb²⁺, Hg₂²⁺.</li>
            <li>Sulfates (SO₄²⁻): soluble <b>except</b> Ba²⁺, Pb²⁺, Ca²⁺, Sr²⁺.</li>
            <li>Hydroxides (OH⁻) &amp; sulfides (S²⁻): <b>insoluble</b> except Group 1 / NH₄⁺ (and Ba²⁺).</li>
            <li>Carbonates (CO₃²⁻) &amp; phosphates (PO₄³⁻): <b>insoluble</b> except Group 1 / NH₄⁺.</li>
          </ul>
        </details>
      </div>
    </VizCard>
  );
}

function rand(min: number, max: number) {
  return min + Math.random() * (max - min);
}
